import React from 'react';
import { Menu } from 'lucide-react';
import { Avatar, RobbieMark } from '../ui';
import { ShellNavGroup, ShellNavItem } from './AppSidebar';

/**
 * Collapsed left nav — the same navy chrome as `AppSidebar` reduced to its
 * icons. It holds its place in the layout; the labels come back either as the
 * hover/focus flyout or, once pinned, as the full in-flow sidebar.
 *
 * Purely presentational: the caller owns pin state and hover intent
 * (`useSidebarRail`). Every control carries its label as `aria-label` +
 * `title`, because at this width the icon is all there is to read.
 */

export interface SidebarRailProps {
  groups: ShellNavGroup[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  /** Sidebar is docked in flow; drives the pin button's pressed state. */
  pinned: boolean;
  onTogglePin: () => void;
  /** Focus target when Escape closes the flyout. */
  pinButtonRef?: React.RefObject<HTMLButtonElement | null>;
  user?: { name: string };
  className?: string;
}

const RailRow: React.FC<{ item: ShellNavItem; active: boolean; onNavigate?: (id: string) => void }> = ({
  item,
  active,
  onNavigate,
}) => {
  const base = 'relative flex h-[38px] w-[38px] items-center justify-center rounded-control [&>svg]:h-4 [&>svg]:w-4';
  const badge =
    item.count !== undefined && item.count > 0 ? (
      <span className="absolute -right-[3px] -top-[3px] min-w-[15px] rounded-pill bg-white/[0.14] px-[3px] text-center font-mono text-[9.5px] font-semibold leading-[14px] text-chrome-text">
        {item.count}
      </span>
    ) : null;

  if (item.soon) {
    return (
      <div
        className={`${base} cursor-default text-chrome-text opacity-55`}
        aria-disabled="true"
        title={`${item.label} — soon`}
      >
        {item.icon}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate?.(item.id)}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      title={item.label}
      className={`${base} ${
        active ? 'bg-chrome-active text-white' : 'text-chrome-text hover:bg-white/5 hover:text-white'
      }`}
    >
      {active && <span className="absolute bottom-s2 left-[-11px] top-s2 w-[2.5px] rounded-[2px] bg-pipeline-4" />}
      {item.icon}
      {badge}
    </button>
  );
};

export const SidebarRail: React.FC<SidebarRailProps> = ({
  groups,
  activeId,
  onNavigate,
  pinned,
  onTogglePin,
  pinButtonRef,
  user,
  className = '',
}) => (
  <aside className={`flex h-full flex-col items-center bg-chrome py-[22px] ${className}`}>
    <button
      ref={pinButtonRef}
      type="button"
      onClick={onTogglePin}
      aria-label={pinned ? 'Collapse navigation' : 'Expand and pin navigation'}
      aria-expanded={pinned}
      aria-pressed={pinned}
      title={pinned ? 'Collapse navigation' : 'Expand and pin navigation'}
      className="flex h-[34px] w-[34px] items-center justify-center rounded-control text-chrome-text hover:bg-white/5 hover:text-white"
    >
      <Menu className="h-[17px] w-[17px]" />
    </button>
    <div className="mb-[24px] mt-[14px]">
      <RobbieMark size="md" glow aria-label="Hello Robbie" />
    </div>
    {groups.map((group, i) => (
      <nav key={group.label} className={`flex flex-col items-center gap-[4px] ${i > 0 ? 'mt-s6' : ''}`}>
        {group.items.map((item) => (
          <RailRow key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} />
        ))}
      </nav>
    ))}
    {user && (
      <div className="mt-auto border-t border-white/[0.08] pt-[12px]">
        <Avatar name={user.name} tone="action" className="h-[30px] w-[30px] text-[12px]" />
      </div>
    )}
  </aside>
);

export default SidebarRail;
